import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import T from 'i18n-react';

import { Sidebar } from '@motorway/motorway-component-library';

import { PromotionsBanner } from 'Components/PromotionsBanner/PromotionsBanner';
import { PromotionsBannerMobile } from 'Components/PromotionsBanner/PromotionsBannerMobile';
import SavedFilters from 'Components/shared/SavedFilters/SavedFilters';
import VehicleFilters from 'Components/VehicleList/filters/Filters';
import { MobilizeRecommendationRow } from 'Components/VehicleList/MobilizeRecommendationRow';
import NoSale from 'Components/VehicleList/NoSale/NoSale';
import NotFoundFilters from 'Components/VehicleList/NotFoundFilters/NotFoundFilters';
import { getVehiclesCount } from 'Components/VehicleList/VehicleList.helpers';
import { VehicleListError } from 'Components/VehicleList/VehicleListError/VehicleListError';
import VehicleListRefreshButton from 'Components/VehicleList/VehicleListRefreshButton/VehicleListRefreshButton';
import VehiclesListBase from 'Components/VehicleList/VehiclesListBase/VehiclesListBase';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { AiCompanionContent } from 'Features/AiCompanion';
import { ANALYTICS_CATEGORIES } from 'Services/analytics/analytics.const';
import { triggerRefreshVehicleListButtonClickedEvent } from 'Services/analytics/events';
import {
	triggerBulkBuyingPromotionMobileBannerClosed,
	triggerBulkBuyingPromotionModalOpened,
} from 'Services/analytics/events/bulkBuyingPromotionEvents';
import { useAiCompanionExperimentation } from 'Services/optimizely/experimentations/useAiCompanionExperimentation';
import { useAppliedFiltersCount, useHasUserSetSort } from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { useListType } from 'Stores/ListStore/ListStore';
import Content from 'Storybook/local/Content/Content';
import { LIST_TYPES } from 'Utilities/consts';
import { useIsMounted } from 'Utilities/hooks/useIsMounted';
import useSaleTimes from 'Utilities/hooks/useSaleTimes';
import { useIsBidCountsStale } from 'Utilities/hooks/vehicleList/useIsBidCountsStale';
import useShouldFetchVehicles from 'Utilities/hooks/vehicleList/useShouldFetchVehicles';
import proPropTypes from 'Utilities/proPropTypes';

import FiltersHeader from './FiltersHeader/FiltersHeader';
import StickyHeader from './FiltersHeader/StickyHeader';
import VehiclesListBackToTop from './VehiclesListBackToTop/VehiclesListBackToTop';
import VehiclesListResults from './VehiclesListResults/VehiclesListResults';
import { vehicleListPropComparer } from './FilterableVehicleList.helpers';

import styles from './FilterableVehicleList.module.scss';

T.setTexts(require('../../texts.json'));

const onPromotionsBannerExplanationClicked = () =>
	triggerBulkBuyingPromotionModalOpened(ANALYTICS_CATEGORIES.MARKETPLACE.VEHICLE_LIST);
const onPromotionsBannerMobileClosed = () =>
	triggerBulkBuyingPromotionMobileBannerClosed(ANALYTICS_CATEGORIES.MARKETPLACE.VEHICLE_LIST);

const FilterableVehicleList = React.memo(
	({
		dealerLatLng,
		dealerPostcode,
		filters,
		isError,
		isLoading,
		isOpenFilters,
		loadMore,
		noSaleProps,
		noVehiclesFound,
		onSidebarCloseClick,
		onVehicleListRefreshButtonClick,
		pagination,
		shouldDisplayRefreshPrompt,
		shouldShowNoSaleMessage,
		togglePanelFilters,
		vehicles,
		vehiclesCount,
	}) => {
		const focusRef = useRef();
		const enableAiCompanion = useAiCompanionExperimentation();
		const listType = useListType();
		const isAiCompanionVisible = listType === LIST_TYPES.aiCompanion;
		const appliedFiltersCount = useAppliedFiltersCount();
		const hasUserSetSort = useHasUserSetSort();

		const isMounted = useIsMounted();
		const [isHeaderSticky, setIsHeaderSticky] = useState(false);
		const {
			minWidth: { breakpointContent: isDesktop },
		} = useBreakpoint();
		const shouldFetchVehicles = useShouldFetchVehicles();
		const isBidCountsStale = useIsBidCountsStale();
		const { isLiveSaleEnabled, isSaleActive } = useSaleTimes();

		const { order, sort } = filters;
		const { hasMore, touched } = pagination;

		const hasVehicles = !!vehicles && vehicles.length > 0;
		const isNoSaleVisible = !shouldFetchVehicles || shouldShowNoSaleMessage;
		const initialLoad = !touched;
		const isLiveSaleActive = isLiveSaleEnabled && isSaleActive;

		const isRefreshButtonVisible = shouldDisplayRefreshPrompt || (isBidCountsStale && hasVehicles);
		const showAiCompanion = enableAiCompanion && isAiCompanionVisible && isLiveSaleActive;

		const onStickyHeaderChange = ({ isHidden, isSticky }) => {
			setIsHeaderSticky(isSticky && !isHidden);
		};

		const handleRefreshButtonClick = () => {
			triggerRefreshVehicleListButtonClickedEvent();
			onVehicleListRefreshButtonClick();
		};

		if (isError && !showAiCompanion) {
			return <VehicleListError />;
		}

		const renderFilters = () => (
			<VehicleFilters
				{...{
					dealerLatLng,
					dealerPostcode,
					filters,
					isLoading,
					sortParams: { order, sort },
					togglePanelFilters,
					vehiclesCount,
				}}
			/>
		);

		const renderTabsContent = () => {
			if (showAiCompanion) {
				return <AiCompanionContent />;
			}

			if (isNoSaleVisible) {
				return (
					<Content>
						<NoSale {...noSaleProps} />
					</Content>
				);
			}

			return (
				<Content ref={focusRef} className={styles.filterableVehicleList} element="main">
					<PromotionsBannerMobile onClose={onPromotionsBannerMobileClosed} />
					<FiltersHeader
						dealerLatLng={dealerLatLng}
						dealerPostcode={dealerPostcode}
						initialLoad={!touched}
						isLoading={isLoading}
						noVehiclesFound={noVehiclesFound}
						onStickyHeaderChange={onStickyHeaderChange}
						sortParams={{ order, sort }}
						togglePanelFilters={togglePanelFilters}
						vehiclesCount={vehiclesCount}
					/>

					{isDesktop ? (
						<StickyHeader onChange={onStickyHeaderChange}>
							<SavedFilters />
						</StickyHeader>
					) : (
						vehiclesCount > 0 && (
							<div
								className={cx(styles.vehicleCount, {
									[styles.dataLoading]: isLoading,
									[styles.preventFocus]: initialLoad,
								})}
							>
								{getVehiclesCount('vehicleList.showingVehicle', vehiclesCount)}
							</div>
						)
					)}

					{isMounted && (
						<Sidebar
							className={styles.sidebar}
							isOpen={isOpenFilters && !isDesktop}
							onClose={onSidebarCloseClick}
							overlay={{ className: styles.sidebarZIndex }}
							sidebar={{
								className: `${styles.sidebar} ${styles.sidebarZIndex}`,
							}}
						>
							<Content className={styles.sidebarContent}>{renderFilters()}</Content>
						</Sidebar>
					)}
					<div className={styles.contentWrapper}>
						<aside className={styles.filtersContainer}>{renderFilters()}</aside>
						<section
							className={cx(styles.vehiclesContainer, {
								[styles.notFound]: noVehiclesFound,
							})}
						>
							<VehicleListRefreshButton
								isEnabled={isRefreshButtonVisible}
								isStickyHeaderVisible={isHeaderSticky}
								onClick={handleRefreshButtonClick}
								text={T.translate(`vehicleList.${!shouldDisplayRefreshPrompt ? 'refreshBidCounts' : 'refreshList'}`)}
							/>
							{noVehiclesFound ? (
								<NotFoundFilters />
							) : (
								<>
									<PromotionsBanner onExplanationClick={onPromotionsBannerExplanationClicked} />
									<MobilizeRecommendationRow
										appliedFiltersCount={appliedFiltersCount}
										hasUserSetSort={hasUserSetSort}
										listType={listType}
									/>
									<VehiclesListResults
										{...{
											hasMore,
											isLoading,
											loadMore,
											vehicles,
										}}
									/>
									{hasVehicles ? <VehiclesListBackToTop focusRef={focusRef} /> : null}
								</>
							)}
						</section>
					</div>
				</Content>
			);
		};

		return <VehiclesListBase>{renderTabsContent()}</VehiclesListBase>;
	},
	vehicleListPropComparer,
);

FilterableVehicleList.defaultProps = {
	vehicles: null,
};

FilterableVehicleList.propTypes = {
	...proPropTypes.sharedVehicleListProps,
	filters: proPropTypes.vehicleFilters.isRequired,
	isOpenFilters: PropTypes.bool.isRequired,
	loadMore: PropTypes.func.isRequired,
	onSidebarCloseClick: PropTypes.func.isRequired,
	pagination: PropTypes.shape({
		hasMore: PropTypes.bool.isRequired,
		touched: PropTypes.bool.isRequired,
	}).isRequired,
	shouldDisplayRefreshPrompt: PropTypes.bool.isRequired,
	shouldShowNoSaleMessage: PropTypes.bool.isRequired,
	vehicles: PropTypes.arrayOf(proPropTypes.vehicleCardList),
};

export default FilterableVehicleList;
